import { action, makeObservable, observable, reaction } from 'mobx';
import { useEffect } from 'react';
import { useTypedSearchParams } from 'react-router-typesafe-routes/dom';
import { API } from '~/api';
import { AppRoutes } from '~/app/app.routes';
import { GridStateModel } from '~/model/model.gridstate';
import { Singleton } from '~/shared/service/service.base';
import { Async } from '~/shared/tools/async';
import { ApiService } from './service.api';
import { DashboardService } from './service.dashboard';

@Singleton()
export class DashboardActivitiesService {
	readonly async = new Async();
	readonly items = observable.array([] as API.AnalyticsListItem[], { deep: false });
	readonly gridState = new GridStateModel('actions');

	@observable totalCount = 0;
	@observable filterActivityType?: API.UserActivityType = undefined;
	@observable filterActivityAction?: API.UserActivityAction = undefined;
	@observable filterActivityValue?: string = undefined;

	constructor(
		private api: ApiService,
		private dashboardSvc: DashboardService,
	) {
		makeObservable(this);
	}

	useSearchParams = () => {
		const [query] = useTypedSearchParams(AppRoutes.DashboardActivities);

		useEffect(() => {
			if (query.type && this.dashboardSvc.uniqueTypes.some(t => t === query.type)) {
				this.setFilterActivityType(null, query.type as API.UserActivityType);
			} else this.setFilterActivityType(null, null);

			if (query.action && this.dashboardSvc.uniqueActions.some(a => a === query.action)) {
				this.setFilterActivityAction(null, query.action as API.UserActivityAction);
			} else this.setFilterActivityAction(null, null);

			if (query.value && this.dashboardSvc.uniqueValues.some(v => v === query.value)) {
				this.setFilterActivityValue(null, query.value);
			} else this.setFilterActivityValue(null, null);
		}, [query.action, query.type, query.value]);
	};

	useActivitiesList = () => {
		useEffect(() => this.reloadList(), []);
		useEffect(() => this.subscribe(this.reloadList), []);
		useEffect(() => this.gridState.subscribe(this.reloadList), []);
	};

	reloadList = () => {
		this.async.call(async () => {
			const list = await this.api.analyticsActivitiesList({
				days: this.dashboardSvc.period,
				limit: this.gridState.pageSize,
				offset: this.gridState.offset,
				type: this.filterActivityType,
				action: this.filterActivityAction,
				value: this.filterActivityValue,
			});

			return () => {
				this.items.replace(list.data.items);
				this.totalCount = list.data.total;
			};
		});
	};

	subscribe(cb: () => void): void | (() => void) {
		return reaction(
			() => [
				this.dashboardSvc.period,
				this.filterActivityType,
				this.filterActivityAction,
				this.filterActivityValue,
			],
			cb,
		);
	}

	@action.bound setFilterActivityType(_event: unknown, type: API.UserActivityType | null) {
		this.filterActivityType = type || undefined;
	}

	@action.bound setFilterActivityAction(_event: unknown, action: API.UserActivityAction | null) {
		this.filterActivityAction = action || undefined;
	}

	@action.bound setFilterActivityValue(_event: unknown, value: string | null) {
		this.filterActivityValue = value || undefined;
	}
}
